import type { ReactNode } from "react";
import type { CardDeckId } from "@bing/shared";
import { CARD_DECK_LIST, CardDeckContext } from "../game/card-decks";
import { prefetchDeck } from "../game/deck-assets";
import { useDeckSample } from "../game/use-card-faces";
import { fa } from "../i18n/fa";
import { Card, LARGE_CARD_BOX, PREVIEW_CARD_BOX } from "./Card";

/**
 * The shared card-deck ("design") picker — a horizontal, scrollable row of FACE
 * previews behaving as a radiogroup. Used by BOTH the per-table Game Settings (the
 * host's choice, rides `tableSettings.deck`) and the per-user offline Settings
 * (persisted via `setCardDeck`). Each option previews that deck's OWN King of Diamonds
 * face — rendered by the shared `Card` inside the deck's context — so what you pick
 * is exactly the front graphic you'll play with.
 *
 * ⚠️ THIS IS THE ONLY PLACE OUTSIDE A CARD TABLE THAT RENDERS A REAL PLAYING CARD, AND THAT IS
 * WHY IT HAS TO GATE ITSELF. The classic `standard` deck's face is a React component from the
 * lazily-loaded artwork chunk (`game/card-faces.ts`); the other three are `<img>` files that need
 * nothing. Until 2026-08-04 the picker rendered the classic preview unconditionally, so opening
 * either host screen before the chunk had landed threw out of `Card` and took the whole app to the
 * ErrorBoundary — which is exactly what a real user hit 25 s into their first-ever session.
 *
 * The gate covers both failure modes: the ordinary race (the chunk is normally requested a few
 * seconds after boot and still has to download over a slow link), and the permanent one — a
 * prefetch that exhausted its retries latches `failed`, and a fresh round is kicked when a screen
 * that needs the artwork is opened again.
 *
 * Since 2026-10-20 every deck's preview is a SAMPLE (`useDeckSample`): the image decks' `KD` alone is
 * fetched (not 52 faces of three decks nobody has picked), the classic one waits on its chunk, and
 * each shows the skeleton until its face is in. PICKING a deck starts the whole of it downloading in
 * the background (`prefetchDeck`), so it is normally in by the time the player reaches a table — and
 * a table that opens first deals «استاندارد» until it is (`deck-assets.ts`).
 */

/** Card-shaped stand-in for the classic preview while its artwork is in flight. Sized to the
 *  `large` card box so the row does not reflow when the real face arrives. */
function LoadingFace({ compact = false }: { compact?: boolean }): ReactNode {
  const box = compact ? PREVIEW_CARD_BOX : LARGE_CARD_BOX;
  return (
    <span
      data-testid="deck-face-loading"
      aria-hidden
      className="deck-face-skeleton block"
      style={{ width: box.w, height: box.h, borderRadius: 6 }}
    />
  );
}

export function DeckPicker({
  value,
  onChange,
  compact = false,
}: {
  value: CardDeckId;
  onChange: (id: CardDeckId) => void;
  /**
   * Start-screen sizing: the SMALL card box (36×50) instead of the large one (50×70), and tighter
   * tile padding. Measured: the row goes 140 px → 101 px, which is a fifth of حکم's whole height
   * budget at 320×568. The per-table «تنظیمات میز» host screen keeps the roomy default — it has a
   * scrolling page to spend, and picking a deck for four other people deserves the bigger preview.
   */
  compact?: boolean;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={fa.online.cardDeck}
      className={
        compact
          ? // FULL WIDTH, like the difficulty row directly above it — same gap-1, same segmented
            // rhythm. The tiles used to be `flex-none` inside an `overflow-x-auto` row, so at 320
            // they measured 210px inside a 252px card and sat as a cluster off to one side while
            // the control above them spanned edge to edge. Four tiles ALWAYS fit here (there are
            // exactly four decks), so the scroller earned nothing and cost the alignment.
            "flex w-full gap-1"
          : // The host's «تنظیمات میز» keeps the scrolling row and its bigger previews — a wider
            // page, and a deck chosen for four other people deserves the larger picture.
            "flex gap-2 overflow-x-auto overflow-y-hidden pb-1"
      }
    >
      {CARD_DECK_LIST.map((d) => {
        const on = value === d.id;
        // Only the artwork-backed deck can be un-previewable; an image deck is always drawable.
        return (
          <button
            key={d.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={d.name}
            onClick={() => {
              prefetchDeck(d.id);
              onChange(d.id);
            }}
            className={`flex cursor-pointer appearance-none flex-col items-center rounded-xl transition-colors ${
              compact
                ? // NO FRAME ON THE START SCREENS: the preview IS a card, and a bordered box around
                  // a picture of a card reads as a second card edge. Selection is carried by the
                  // gold wash plus the deck's own name turning gold, both already here.
                  //
                  // ⚠️ `border-0` IS THE REMOVAL, not the absence of a border class. This tile is a
                  // <button>, and the UA gives buttons `border: 2px outset` — measured on the
                  // deployed build, simply dropping `border-2 border-solid` swapped a deliberate
                  // 2px frame for a 2px BEVEL, which is worse than what was there.
                  // ⚠️ And the background lives ONLY in these branches: a `bg-transparent` on the
                  // shared base won the cascade over `bg-gold-deep/15` (same specificity, Tailwind's
                  // own sheet order decides), so the selected tile measured fully transparent.
                  `min-w-0 flex-1 gap-0.5 border-0 p-0.5 ${
                    on ? "bg-gold-deep/15" : "bg-transparent hover:bg-surface-variant/40"
                  }`
                : // The host's «تنظیمات میز» keeps its framed tiles — a different screen, untouched.
                  `flex-none gap-1 border-2 border-solid bg-transparent p-1.5 ${
                    on
                      ? "border-gold-deep bg-gold-deep/10"
                      : "border-outline-variant hover:bg-surface-variant/40"
                  }`
            }`}
          >
            {/* This deck's OWN King-of-Diamonds face — a court card + a coloured suit, so
                the preview shows the design's court style AND reveals the four-colour deck
                (blue diamonds) vs a classic one (red), not just a plain black-spade ace.
                The CHOICE is never gated — only the picture is: a player must be able to select
                any deck whether or not its preview has downloaded yet. */}
            <DeckSample deck={d.id} compact={compact} />
            <span
              className={`whitespace-nowrap font-bold ${compact ? "text-[10px] leading-tight" : "text-caption"} ${
                on ? "text-gold-deep" : "text-on-surface-variant"
              }`}
            >
              {d.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** One deck's preview: its `KD` once that one face is in, the skeleton until then. */
function DeckSample({ deck, compact }: { deck: CardDeckId; compact: boolean }): ReactNode {
  const { shown } = useDeckSample(deck, "KD");
  if (!shown) return <LoadingFace compact={compact} />;
  return (
    <CardDeckContext.Provider value={deck}>
      {compact ? <Card id="KD" preview sample /> : <Card id="KD" large sample />}
    </CardDeckContext.Provider>
  );
}
